import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { RootState } from '@/src/store'
import { DeviceType } from '@/src/types'
import { getUserProfile } from '@/src/api/services/visaService'
import {
    UserProfileResponse,
    UserProfileData
} from '@/src/types/visa/userProfile'

interface UserProfileState {
    data: UserProfileData | null
    isLoading: boolean
    error: string | null
}

const initialState: UserProfileState = {
    data: null,
    isLoading: false,
    error: null
}

export const fetchUserProfile = createAsyncThunk(
    'userProfile/fetchUserProfile',
    async (deviceType: DeviceType) => {
        const response = await getUserProfile(deviceType)
        return response.data
    }
)

const userProfileSlice = createSlice({
    name: 'userProfile',
    initialState,
    reducers: {
        clearUserProfile: (state) => {
            state.data = null
            state.error = null
        }
    },
    extraReducers(builder) {
        builder
            .addCase(fetchUserProfile.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(fetchUserProfile.fulfilled, (state, action) => {
                state.isLoading = false
                if (
                    action.payload &&
                    action.payload.travellerDetails &&
                    action.payload.travellerDetails.length > 0
                ) {
                    const traveller = action.payload.travellerDetails[0]
                    const { personalDetails, contactInfo, docDetails } =
                        traveller

                    // Extract phone number and country code
                    const phoneNumber =
                        contactInfo?.phoneNumbers?.[0]?.mobileNumber || ''
                    const isdCode =
                        contactInfo?.phoneNumbers?.[0]?.mobileCountryCode || ''

                    // Extract passport details
                    const passportNumber = docDetails?.[0]?.docNumber || ''
                    const passportExpiry = docDetails?.[0]?.expiryDate || ''

                    // Convert timestamp to date string
                    const dateOfBirth = personalDetails?.dateOfBirth
                        ? new Date(personalDetails.dateOfBirth)
                              .toISOString()
                              .split('T')[0]
                        : ''

                    state.data = {
                        firstName: personalDetails?.firstName || '',
                        lastName: personalDetails?.lastName || '',
                        email: personalDetails?.primaryEmail || '',
                        gender: personalDetails?.gender || '',
                        dateOfBirth,
                        phoneNumber,
                        isdCode,
                        passportNumber,
                        passportExpiry
                    }
                }
            })
            .addCase(fetchUserProfile.rejected, (state, action) => {
                state.isLoading = false
                state.error =
                    action.error.message || 'Failed to fetch user profile'
            })
    }
})

export const { clearUserProfile } = userProfileSlice.actions
export const getUserProfileData = (state: RootState) => state.userProfile.data
export const getUserProfileLoading = (state: RootState) =>
    state.userProfile.isLoading
export const getUserProfileError = (state: RootState) => state.userProfile.error

export default userProfileSlice.reducer
